"use client";

import { useEffect, useRef, useState } from "react";
import { Cloud, Droplet, Leaf, Sun } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Stat = {
  icon: "leaf" | "droplet" | "cloud" | "sun";
  value: number;
  display: string;
  suffix: string;
  unit: string;
  description: string;
  offset: string;
};
const stats: Stat[] = [{
  icon: "droplet",
  value: 29,
  display: "29",
  suffix: "+",
  unit: "Years Expertise",
  description: "Deep-rooted resort management experience ensuring institutional-grade operational excellence.",
  offset: ""
}, {
  icon: "sun",
  value: 100,
  display: "100",
  suffix: "%",
  unit: "Legal Compliance",
  description: "Full PROFECO and SAT registration for transparent, risk-free asset management and exits.",
  offset: ""
}, {
  icon: "cloud",
  value: 45,
  display: "45",
  suffix: "M",
  unit: "Assets Managed",
  description: "Total value of high-end hospitality and residential portfolios under our elite supervision.",
  offset: ""
}, {
  icon: "leaf",
  value: 0,
  display: "0",
  suffix: "$",
  unit: "Upfront Costs",
  description: "Our commitment to transparency: zero initial fees for timeshare and fractional exit assessments.",
  offset: ""
}];
const iconMap = {
  leaf: Leaf,
  droplet: Droplet,
  cloud: Cloud,
  sun: Sun
};
function CountUp({
  value,
  display,
  suffix,
  isVisible
}: {
  value: number;
  display: string;
  suffix: string;
  isVisible: boolean;
}) {
  const [current, setCurrent] = useState(0);
  const hasAnimated = useRef(false);
  useEffect(() => {
    if (!isVisible || hasAnimated.current) return;
    hasAnimated.current = true;
    const duration = 1500;
    const start = performance.now();
    let frame: number;
    const animate = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCurrent(value * eased);
      if (progress < 1) {
        frame = requestAnimationFrame(animate);
      } else {
        setCurrent(value);
      }
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [isVisible, value, display]);
  const isDecimal = display.includes(".");
  const formatted = isDecimal ? current.toFixed(1) : Math.round(current).toString();
  return <span>
      {formatted}
      <span className="text-primary">{suffix}</span>
    </span>;
}
export default function TheImiAdvantage() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const quote = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const grid = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="5062" id="the-imi-advantage" className="dark bg-background text-white py-40 px-6 md:px-10 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div ref={header.ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 mb-24 md:mb-32 transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-7">
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-primary mb-8">
              &mdash; 02 / The IMI Advantage
            </p>
            <h2 className="font-default font-semibold text-white leading-[1.1] tracking-tighter text-5xl md:text-6xl lg:text-7xl xl:text-8xl">
              <span className="inline-block w-2 h-12 md:h-16 bg-primary mr-6 align-middle" />
              Precision in
              <br />
              Management.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-32">
            <p className="font-default text-lg md:text-xl text-white leading-relaxed">
              At IMI Elite Management, we bridge the gap between high-stakes real estate legalities and high-end hospitality. Every asset is managed with institutional authority and a commitment to absolute transparency.
            </p>
          </div>
        </div>

        <div ref={grid.ref} className="relative grid grid-cols-1 md:grid-cols-2 gap-x-12 lg:gap-x-24 gap-y-20 md:gap-y-12 mb-32">
          <div className="hidden md:block absolute top-1/2 left-0 right-0 h-px bg-border -translate-y-1/2" aria-hidden="true" />
          <div className="hidden md:block absolute top-0 bottom-0 left-1/2 w-px bg-border -translate-x-1/2" aria-hidden="true" />

          {stats.map((stat, i) => {
          const Icon = iconMap[stat.icon];
          return <div key={i} data-index={i} className={`relative ${stat.offset} transition-all ease-out ${grid.isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4"}`} style={{
            transitionDuration: "600ms",
            transitionDelay: `${i * 100}ms`
          }}>
                <div className="flex flex-col">
                  <div className="size-12 flex items-center justify-center border border-border mb-8">
                    <Icon className="size-5 text-primary" strokeWidth={1.5} />
                  </div>

                  <div className="font-serif font-bold text-white leading-none tracking-tighter mb-6" style={{
                fontSize: "clamp(4rem, 8vw, 7rem)"
              }}>
                    <CountUp value={stat.value} display={stat.display} suffix={stat.suffix} isVisible={grid.isVisible} />
                  </div>

                  <p className="font-mono text-[0.75rem] font-semibold tracking-[0.2em] uppercase text-primary mb-5">
                    {stat.unit}
                  </p>

                  <p className="font-default text-base md:text-lg text-white leading-relaxed max-w-sm">
                    {stat.description}
                  </p>
                </div>
              </div>;
        })}
        </div>

        <div ref={quote.ref} className={`flex flex-col items-center text-center pt-12 transition-all duration-1000 ease-out ${quote.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="w-px h-16 bg-primary mb-10" aria-hidden="true" />
          <p className="font-serif italic font-light text-2xl md:text-3xl lg:text-4xl text-white leading-snug max-w-3xl">
            &mdash; Every asset is a legacy protected by precision.
          </p>
          <p className="font-mono text-xs tracking-[0.35em] uppercase text-primary mt-10">
            IMI ELITE MANAGEMENT
          </p>
        </div>
      </div>
      <div className="absolute right-0 bottom-0 opacity-[0.03] font-serif text-[20rem] leading-none select-none pointer-events-none -rotate-90 translate-x-1/3">
        IMI
      </div>
    </section>;
}